import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import { trackRouter } from "vue-gtag-next";
import admin_console_routes from "@/apps/admin-console/router";
import person_registration_routes from "@/apps/person-registration/router";
import merchant3_routes from "@/apps/merchant3/router";
import cl_routes from "@/apps/credit-line/router";
import core_app_onboarding_routes from "@/apps/core-app-onboarding/router";
import login_routes from "@/apps/person-registration/login/router";
import dashboard_routes from "@/apps/dashboard/router";
import { personLoginGuard } from "@/apps/person-registration/login/router/routeGuards";
import PersonLogin from "@/apps/person-registration/login/views/PersonLoginView.vue";
import PageNotFound from "@/shared-components/common/PageNotFound.vue";
import { getProduct, PRODUCT } from "@/shared-helpers/product";
import { datadogRum } from "@datadog/browser-rum";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { initializeRum, RumApp } from "@/shared-services/datadog-rum.service";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: PersonLogin,
    beforeEnter: personLoginGuard,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "PageNotFound",
    component: PageNotFound,
  },
];

routes.push(...admin_console_routes);
routes.push(...person_registration_routes);
routes.push(...merchant3_routes);
routes.push(...login_routes);
routes.push(...cl_routes);
routes.push(...dashboard_routes);
routes.push(...core_app_onboarding_routes);

let lastProduct;

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  lastProduct = getProduct();
  if (document)
    document.title = to.meta?.title ? String(to.meta.title) : "PayJoy";

  if (from.query.afterRegistration && !to.query.afterRegistration) {
    if (to.path === from.path) {
      return;
    }
    next({
      path: to.path,
      query: { afterRegistration: from.query.afterRegistration },
    });
  }

  next();
});

/*
  TODO:
  To be deleted after translations are migrated to db.
  This is to prevent missing translations when changing from M3 to SMILE and back
*/
let rumInitialized = false;

router.afterEach(async (to, from) => {
  const newProduct = getProduct();

  if (newProduct == PRODUCT.SMILE && newProduct != lastProduct) {
    window.location.reload();
  }

  handleRumTracking(to, from, newProduct);
});

const handleRumTracking = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  product: PRODUCT
): void => {
  const enableRUM = to.meta.enableRUM ?? false;

  // Do nothing when RUM is disabled for the current route.
  if (!enableRUM) {
    return;
  }

  if (!rumInitialized) {
    // Initialize RUM. M3 is the default.
    if (product === "CORE_APP_ONBOARDING" || product === "CREDIT_LINE") {
      initializeRum(RumApp.CONSUMER_APP);
    } else {
      initializeRum(RumApp.M3);
    }
    rumInitialized = true;
  }

  let sessionWasStopped = false;

  // Stop active session when navigating back to M3 home,
  // otherwise, keep using the current session.
  if (
    (to.name === RouteName.M3Welcome && from.name) ||
    (to.name === RouteName.M3Ecommerce && from.name === undefined)
  ) {
    datadogRum.stopSessionReplayRecording();
    datadogRum.stopSession();
    datadogRum.clearUser();
    sessionWasStopped = true;
  }

  // Rehydrate an active RUM session with existing data to preserve it.
  // See: https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#contexts-life-cycle
  if (!sessionWasStopped) {
    const dataDogRumSessionType = sessionStorage.getItem(
      SessionStorageEnum.DataDogRumSessionType
    );
    const cartTag = sessionStorage.getItem(SessionStorageEnum.CartTag);
    const activeFeatures = JSON.parse(
      sessionStorage.getItem(SessionStorageEnum.CartActiveFeatures) ?? "[]"
    );
    const productVersion = sessionStorage.getItem(
      SessionStorageEnum.CartProductVersion
    );
    const personRegistrationTag = sessionStorage.getItem(
      SessionStorageEnum.CartRegistrationTag
    );

    if (cartTag) {
      datadogRum.setUserProperty("cartTag", cartTag);
    }
    if (dataDogRumSessionType) {
      datadogRum.setUserProperty("sessionType", dataDogRumSessionType);
    }
    if (productVersion) {
      datadogRum.setUserProperty("productVersion", productVersion);
    }
    if (activeFeatures.length) {
      datadogRum.setUserProperty("activeFeatures", activeFeatures);
    }
    if (personRegistrationTag) {
      datadogRum.setUserProperty("registrationTag", personRegistrationTag);
    }
  }

  datadogRum.startSessionReplayRecording();
  datadogRum.startView((to.name as string) || to.path);
};

trackRouter(router);
export default router;
